<script setup>
import ArrowDownIcon from '@iconify-vue/ph/arrow-down';
import ArrowUpIcon from '@iconify-vue/ph/arrow-up';
import { FlexRender } from '@tanstack/vue-table';
import { useResizeObserver } from '@vueuse/core';
import { nextTick, onMounted, provide, ref, useTemplateRef, watch } from 'vue';

const {
    table,
    withFooter = false,
    withRowNumber = false,
    forceScroll = false,
    renderRowSubComponent = null,
    wrapHeaders = false,
} = defineProps({
    table: {
        required: true,
        type: Object,
    },
    withFooter: {
        required: false,
        type: Boolean,
        default: false,
    },
    withRowNumber: {
        required: false,
        type: Boolean,
        default: false,
    },
    forceScroll: {
        required: false,
        type: Boolean,
        default: false,
    },
    renderRowSubComponent: {
        required: false,
        type: Function,
        default: null,
    },
    wrapHeaders: {
        required: false,
        type: Boolean,
        default: false,
    },
});

const tableElement = useTemplateRef('tableElement');

const pinnedRight = ref({ offsets: {}, firstId: null });

provide('pinnedRight', pinnedRight);

function updatePinnedRight() {
    const columns = table.getRightVisibleLeafColumns?.() ?? [];

    if (!columns.length) {
        pinnedRight.value = { offsets: {}, firstId: null };

        return;
    }

    const offsets = {};

    let accumulated = 0;

    for (let index = columns.length - 1; index >= 0; index--) {
        offsets[columns[index].id] = accumulated;

        const headerCell = tableElement.value?.querySelector(
            `thead th[data-column-id="${columns[index].id}"]`,
        );

        accumulated += headerCell?.getBoundingClientRect().width ?? 0;
    }

    pinnedRight.value = { offsets, firstId: columns[0].id };
}

function pinnedRightStyle(column) {
    if (!column.getIsPinned()) {
        return {};
    }

    return { right: `${pinnedRight.value.offsets[column.id] ?? 0}px` };
}

function isFirstPinnedRight(column) {
    return column.getIsPinned() && pinnedRight.value.firstId === column.id;
}

onMounted(() => nextTick(updatePinnedRight));

useResizeObserver(tableElement, () => updatePinnedRight());

watch(
    () =>
        table
            .getVisibleLeafColumns()
            .map((column) => column.id)
            .join(','),
    () => nextTick(updatePinnedRight),
);
</script>

<template>
    <div
        class="max-3xl:h-full max-3xl:max-h-[600px] max-3xl:overflow-x-auto relative"
        :class="{ '!max-h-[600px] !overflow-x-auto': forceScroll }"
    >
        <table
            ref="tableElement"
            class="relative h-fit w-full table-auto border-collapse text-left"
        >
            <thead
                class="max-3xl:top-0 sticky top-[calc(4rem_+_var(--data-table-filters-height,0px))] z-10 bg-white text-xs uppercase"
                :class="{ '!top-0': forceScroll }"
            >
                <tr
                    class="h-12"
                    v-for="headerGroup in table.getHeaderGroups()"
                    :key="headerGroup.id"
                >
                    <th
                        v-if="withRowNumber"
                        class="h-[inherit] p-0"
                        style="width: 0"
                    >
                        <div
                            class="h-12 border-t border-b border-gray-200 px-4 py-2"
                        >
                            &nbsp;
                        </div>
                    </th>

                    <template
                        v-for="header in headerGroup.headers"
                        :key="header.id"
                    >
                        <template
                            v-if="header.column.columnDef.headerCustom === true"
                        >
                            <FlexRender
                                v-if="!header.isPlaceholder"
                                :render="header.column.columnDef.header"
                                :props="header.getContext()"
                            />
                        </template>

                        <th
                            v-else
                            scope="col"
                            class="h-[inherit] p-0"
                            :data-column-id="header.column.id"
                            :class="{
                                'whitespace-nowrap': !wrapHeaders,
                                'cursor-pointer': header.column.getCanSort(),
                                'sticky bg-white': header.column.getIsPinned(),
                                'before:pointer-events-none before:absolute before:top-0 before:-left-[5px] before:block before:h-full before:w-[5px] before:bg-gradient-to-l before:from-gray-200 before:to-gray-50 before:opacity-100 before:content-[\'\']':
                                    isFirstPinnedRight(header.column),
                            }"
                            :style="{
                                width: `${header.column.columnDef.width}px`,
                                ...pinnedRightStyle(header.column),
                            }"
                            @click="
                                (event) =>
                                    header.column.getToggleSortingHandler()?.(
                                        event,
                                    )
                            "
                        >
                            <div
                                class="border-t border-b border-gray-200 px-4 py-3"
                                :class="{
                                    'h-12': !wrapHeaders,
                                    'h-full': wrapHeaders,
                                    flex: wrapHeaders,
                                    'items-center': wrapHeaders,
                                    'text-right':
                                        header.column.columnDef.headerAlign ===
                                        'right',
                                }"
                            >
                                <div
                                    v-if="
                                        typeof header.column.columnDef
                                            .header === 'object' &&
                                        header.column.columnDef.header
                                            .__v_isVNode
                                    "
                                    :class="{
                                        'h-6': !wrapHeaders,
                                        'h-auto': wrapHeaders,
                                    }"
                                >
                                    <FlexRender
                                        v-if="!header.isPlaceholder"
                                        :render="header.column.columnDef.header"
                                        :props="header.getContext()"
                                    />
                                </div>

                                <div
                                    v-else
                                    :class="[
                                        'inline-flex items-center gap-1',
                                        header.column.columnDef.headerClasses,
                                        {
                                            'h-6': !wrapHeaders,
                                        },
                                    ]"
                                >
                                    <FlexRender
                                        v-if="!header.isPlaceholder"
                                        :render="header.column.columnDef.header"
                                        :props="header.getContext()"
                                    />

                                    <div
                                        v-if="header.column.getCanSort()"
                                        class="inline-flex"
                                    >
                                        <ArrowUpIcon
                                            class="h-6 w-3"
                                            :class="{
                                                'opacity-100':
                                                    header.column.getIsSorted() ===
                                                    'asc',
                                                'opacity-40':
                                                    header.column.getIsSorted() !==
                                                    'asc',
                                            }"
                                        />

                                        <ArrowDownIcon
                                            class="h-6 w-3"
                                            :class="{
                                                'opacity-100':
                                                    header.column.getIsSorted() ===
                                                    'desc',
                                                'opacity-40':
                                                    header.column.getIsSorted() !==
                                                    'desc',
                                            }"
                                        />
                                    </div>
                                </div>
                            </div>
                        </th>
                    </template>
                </tr>
            </thead>

            <tbody>
                <template
                    :key="row.id"
                    v-for="(row, index) in table.getRowModel().rows"
                >
                    <tr
                        class="text-sm text-gray-500 duration-150"
                        :class="{
                            'bg-white': index % 2 && !row.parentId,
                            'bg-gray-50': !(index % 2) && !row.parentId,
                            'border-b border-gray-300 bg-white': row.parentId,
                            '!border-b-2 !border-gray-400':
                                row.getIsExpanded() ||
                                (row.parentId &&
                                    row.getParentRow().subRows[
                                        row.getParentRow().subRows.length - 1
                                    ] === row),
                        }"
                    >
                        <td v-if="withRowNumber" style="width: 0">
                            <div
                                class="flex h-full items-center justify-center p-4 text-center whitespace-nowrap"
                            >
                                <div class="text-lg font-bold">
                                    {{
                                        table.getState().pagination.pageIndex *
                                            table.getState().pagination
                                                .pageSize +
                                        index +
                                        1
                                    }}
                                </div>
                            </div>
                        </td>

                        <td
                            v-for="cell in row.getVisibleCells()"
                            :key="cell.id"
                            class="min-h-[60px] p-0"
                            :class="{
                                'bg-white':
                                    (cell.column.getIsPinned() && index % 2) ||
                                    row.parentId,
                                'bg-gray-50':
                                    cell.column.getIsPinned() &&
                                    !(index % 2) &&
                                    !row.parentId,
                                sticky: cell.column.getIsPinned(),
                                'before:pointer-events-none before:absolute before:top-0 before:-left-[5px] before:block before:h-full before:w-[5px] before:bg-gradient-to-l before:from-gray-200 before:to-gray-50 before:opacity-100 before:content-[\'\']':
                                    isFirstPinnedRight(cell.column),
                            }"
                            :style="pinnedRightStyle(cell.column)"
                        >
                            <FlexRender
                                :render="cell.column.columnDef.cell"
                                :props="cell.getContext()"
                            />
                        </td>
                    </tr>
                    <tr v-if="renderRowSubComponent && row.getIsExpanded()">
                        <td :colspan="row.getVisibleCells().length">
                            <component :is="renderRowSubComponent(row)" />
                        </td>
                    </tr>
                </template>
            </tbody>

            <tfoot v-if="withFooter" class="mt-12">
                <tr
                    v-for="footerGroup in table.getFooterGroups()"
                    :key="footerGroup.id"
                    class="border-t border-gray-200 text-sm text-gray-500"
                >
                    <th v-if="withRowNumber" class="px-4 py-3">&nbsp;</th>

                    <td
                        v-for="header in footerGroup.headers"
                        :key="header.id"
                        class="min-h-[60px] p-0"
                    >
                        <FlexRender
                            :render="header.column.columnDef.footer"
                            :props="header.getContext()"
                        />
                    </td>
                </tr>
            </tfoot>
        </table>
    </div>
</template>
